import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { IoDocumentText, IoList, IoGrid, IoTrash } from "react-icons/io5";
import { FaFileExcel } from "react-icons/fa";
import { toast } from "react-toastify";
import TextEdit from "../../molecules/TextEdit";
import { useSignal } from "@preact/signals-react";
import useFetch from "../../../hooks/useFetch";
import PageForm from "../../templates/PageForm";
import NewTooltip from "../../atoms/NewTooltip";
import ATable, { ATableHeader } from "../../organisms/ATable";
import Dialog from "../../organisms/Dialog";
import { exportToExcel } from "../../../services/ExcelExportService";
import { deleteInstallment, deleteDocument } from "../../../services/StructuredOperationsService";

const API_URL = process.env.REACT_APP_CONVERT_XML_API_URL || '';
const API_KEY = process.env.REACT_APP_API_KEY || '';

type Receivable = {
    receivable_number: string;
    due_date: string;
    amount: number;
    v_pag?: string;
    nat_op?: string;
    ind_pag?: string;
};

type Document = {
    assignor_name: string;
    assignor_cnpj: string;
    document_id: string;
    document_title: string;
    cfop: string;
    issue_date: string;
    xml_size_bytes: number;
    receivables: Receivable[];
    debtor_cpf_cnpj: string;
    debtor_name: string;
    debtor_email: string;
    address_street: string;
    address_number: string;
    address_complement: string;
    address_district: string;
    address_ibge_code: string;
    address_city: string;
    address_state: string;
    address_zip_code: string;
    address_country: string;
    address_phone: string;
};

type Operation = {
    operation_code: string;
    total_documents: number;
    total_receivables: number;
    total_amount: number;
    processing_date: string;
    s3_location: string;
    documents: Document[];
    notes: string;
};

const OperationPageView: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const apiVersion = useSignal('');
    const viewMode = useSignal < 'detail' | 'list' > ('list');
    const showDeleteDialog = useSignal(false);
    const deleteAction = useSignal < 'document' | 'installment' | null > (null);
    const selectedDocument = useSignal < string > ('');
    const selectedInstallment = useSignal < string > ('');
    const isDeleting = useSignal(false);
    const operation = useSignal < Operation > ({
        operation_code: "",
        total_documents: 0,
        total_receivables: 0,
        total_amount: 0,
        processing_date: "",
        s3_location: "",
        documents: [],
        notes: ""
    });

    const { isLoading, data, error, isTokenError } = useFetch(`${API_URL}/operations/${id}`, [{ key: 'X-API-Key', value: API_KEY }]);

    console.log('useFetch state:', { isLoading, data, error, isTokenError, id, API_URL });

    if (isTokenError) {
        navigate("/denied");
    }

    if (error) {
        navigate("/network-error");
    }

    useEffect(() => {
        if (data) {
            console.log('Raw API data:', data);
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;

            // Suportar ambos os formatos: novo (com operation aninhado) e antigo (direto)
            const operationCode = data.operation?.operation_code || data.operation_code || '';
            const totalAmount = data.operation?.total_amount || data.total_amount || 0;

            operation.value = {
                operation_code: operationCode,
                total_documents: data.operation?.total_documents || data.total_documents || 0,
                total_receivables: data.operation?.total_receivables || data.total_receivables || 0,
                total_amount: totalAmount,
                processing_date: data.operation?.processing_date || data.processing_date || "",
                s3_location: data.operation?.s3_location || data.s3_location || "",
                documents: data.documents || [],
                notes: data.notes || ''
            };
            console.log('Operation state after update:', operation.value);
        }
    }, [data]);

    const reloadOperationData = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_URL}/operations/${id}`, {
                headers: {
                    'X-API-Key': API_KEY,
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const newData = await response.json();
                apiVersion.value = `${newData.apiName} - ${newData.apiVersion}`;

                // Suportar ambos os formatos: novo (com operation aninhado) e antigo (direto)
                const operationCode = newData.operation?.operation_code || newData.operation_code || '';
                const totalAmount = newData.operation?.total_amount || newData.total_amount || 0;

                operation.value = {
                    operation_code: operationCode,
                    total_documents: newData.operation?.total_documents || newData.total_documents || 0,
                    total_receivables: newData.operation?.total_receivables || newData.total_receivables || 0,
                    total_amount: totalAmount,
                    processing_date: newData.operation?.processing_date || newData.processing_date || "",
                    s3_location: newData.operation?.s3_location || newData.s3_location || "",
                    documents: newData.documents || [],
                    notes: newData.notes || ''
                };
            }
        } catch (error) {
            console.error('Error reloading data:', error);
        }
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatBytes = (bytes: number) => {
        return `${(bytes / 1024).toFixed(2)} KB`;
    };

    const handleDeleteDocument = (documentId: string) => {
        deleteAction.value = 'document';
        selectedDocument.value = documentId;
        showDeleteDialog.value = true;
    };

    const handleDeleteInstallment = (documentId: string, installmentNumber: string) => {
        deleteAction.value = 'installment';
        selectedDocument.value = documentId;
        selectedInstallment.value = installmentNumber;
        showDeleteDialog.value = true;
    };

    const confirmDelete = async () => {
        console.log('confirmDelete called');
        console.log('operation.value:', operation.value);
        console.log('operation.value.operation_code:', operation.value.operation_code);
        console.log('deleteAction.value:', deleteAction.value);
        console.log('selectedDocument.value:', selectedDocument.value);
        console.log('selectedInstallment.value:', selectedInstallment.value);

        isDeleting.value = true;
        try {
            if (deleteAction.value === 'document') {
                const request = {
                    action: 'delete_document' as const,
                    operation_code: operation.value.operation_code,
                    document_id: selectedDocument.value
                };
                console.log('Delete document request:', request);

                const response = await deleteDocument(request);

                toast.success(response.message);

                if (response.operation_deleted) {
                    navigate('/structuring/operations/0/12');
                } else {
                    await reloadOperationData();
                }
            } else if (deleteAction.value === 'installment') {
                const request = {
                    action: 'delete_installment' as const,
                    operation_code: operation.value.operation_code,
                    document_id: selectedDocument.value,
                    installment_number: selectedInstallment.value
                };
                console.log('Delete installment request:', request);

                const response = await deleteInstallment(request);

                toast.success(response.message);
                await reloadOperationData();
            }
        } catch (error: any) {
            console.error('Delete error:', error);
            toast.error(error.message || 'Erro ao deletar');
        } finally {
            isDeleting.value = false;
            showDeleteDialog.value = false;
            deleteAction.value = null;
            selectedDocument.value = '';
            selectedInstallment.value = '';
        }
    };

    const exportToExcelHandler = () => {
        const exportData: any[] = [];

        operation.value.documents.forEach(document => {
            document.receivables.forEach(receivable => {
                exportData.push({
                    indPag: receivable.ind_pag || '',
                    natOp: receivable.nat_op || '',
                    vPag: receivable.v_pag || '',
                    operacao: operation.value.operation_code,
                    cedente_nome: document.assignor_name,
                    cedente_cnpj: document.assignor_cnpj,
                    nfe: document.document_id,
                    titulo: document.document_title,
                    cpf_cnpj: document.debtor_cpf_cnpj,
                    nome: document.debtor_name,
                    logradouro: document.address_street,
                    numero: document.address_number,
                    complemento: document.address_complement,
                    bairro: document.address_district,
                    cod_ibge: document.address_ibge_code,
                    municipio: document.address_city,
                    uf: document.address_state,
                    cep: document.address_zip_code,
                    pais: document.address_country,
                    telefone: document.address_phone,
                    email: document.debtor_email,
                    cfop: document.cfop,
                    data_emissao: formatDate(document.issue_date),
                    ndup: receivable.receivable_number,
                    vdup: receivable.amount,
                    dvenc: receivable.due_date.split('T')[0].split('-').reverse().join('/'),
                    valor_corrigido: '',
                    vencimento_corrigido: '',
                    parcela_adquirida: ''
                });
            });
        });

        const exportHeaders = [
            { id: 'indPag', title: 'indPag', field: 'indPag', canSort: false },
            { id: 'natOp', title: 'natOp', field: 'natOp', canSort: false },
            { id: 'vPag', title: 'vPag', field: 'vPag', canSort: false },
            { id: 'operacao', title: 'Operação', field: 'operacao', canSort: false },
            { id: 'cedente_nome', title: 'Cedente Nome', field: 'cedente_nome', canSort: false },
            { id: 'cedente_cnpj', title: 'Cedente CNPJ', field: 'cedente_cnpj', canSort: false },
            { id: 'nfe', title: 'NFe', field: 'nfe', canSort: false },
            { id: 'titulo', title: 'Titulo', field: 'titulo', canSort: false },
            { id: 'cpf_cnpj', title: 'Sacado CNPJ/CPF', field: 'cpf_cnpj', canSort: false },
            { id: 'nome', title: 'Sacado Nome', field: 'nome', canSort: false },
            { id: 'logradouro', title: 'Logradouro', field: 'logradouro', canSort: false },
            { id: 'numero', title: 'Numero', field: 'numero', canSort: false },
            { id: 'complemento', title: 'Complemento', field: 'complemento', canSort: false },
            { id: 'bairro', title: 'bairro', field: 'bairro', canSort: false },
            { id: 'cod_ibge', title: 'CodIBGE', field: 'cod_ibge', canSort: false },
            { id: 'municipio', title: 'municipio', field: 'municipio', canSort: false },
            { id: 'uf', title: 'UF', field: 'uf', canSort: false },
            { id: 'cep', title: 'CEP', field: 'cep', canSort: false },
            { id: 'pais', title: 'Pais', field: 'pais', canSort: false },
            { id: 'telefone', title: 'Telefone', field: 'telefone', canSort: false },
            { id: 'email', title: 'Email', field: 'email', canSort: false },
            { id: 'cfop', title: 'CFOP', field: 'cfop', canSort: false },
            { id: 'data_emissao', title: 'Data Emissão', field: 'data_emissao', canSort: false },
            { id: 'ndup', title: 'nDup', field: 'ndup', canSort: false },
            { id: 'vdup', title: 'vDup', field: 'vdup', canSort: false },
            { id: 'dvenc', title: 'dVenc', field: 'dvenc', canSort: false },
            { id: 'valor_corrigido', title: 'Valor Corrigido', field: 'valor_corrigido', canSort: false },
            { id: 'vencimento_corrigido', title: 'vencimento corrigido', field: 'vencimento_corrigido', canSort: false },
            { id: 'parcela_adquirida', title: 'Parcela adquirida', field: 'parcela_adquirida', canSort: false }
        ];

        exportToExcel(exportHeaders, exportData, `operacao-${operation.value.operation_code}.csv`);
    };

    const tableHeaders: ATableHeader[] = [
        { id: 'document_title', title: 'Título', field: 'document_title', canSort: false, width: '200px' },
        { id: 'receivable_number', title: 'Parcela', field: 'receivable_number', canSort: false, width: '100px', class: 'text-center' },
        { id: 'due_date', title: 'Vencimento', field: 'due_date', canSort: false, width: '120px', class: 'text-center' },
        { id: 'amount', title: 'Valor', field: 'amount', canSort: false, width: '120px', class: 'text-center' },
        { id: 'cedente_name', title: 'Cedente', field: 'assignor_name', canSort: false, width: '200px' },
        { id: 'debtor_name', title: 'Sacado', field: 'debtor_name', canSort: false, width: '200px' },
        { id: 'actions', title: 'Ações', field: 'actions', canSort: false, width: '100px', class: 'text-center' }
    ];

    const renderTableView = () => {
        const expandedData: any[] = [];

        operation.value.documents.forEach(document => {
            document.receivables.forEach((receivable, receivableIndex) => {
                expandedData.push({
                    document_title: document.document_title,
                    document_id: document.document_id,
                    receivable_number: receivable.receivable_number,
                    due_date: receivable.due_date.split('T')[0].split('-').reverse().join('/'),
                    amount: formatCurrency(receivable.amount),
                    assignor_name: document.assignor_name,
                    debtor_name: document.debtor_name,
                    receivable_raw: receivable
                });
            });
        });

        return (
            <ATable headers={tableHeaders} autoResize={true} marginTop={350}>
                {expandedData.map((row, index) => (
                    <tr key={index} className={`${index % 2 === 0 ? 'bg-slate-100 dark:bg-slate-800' : 'dark:bg-slate-900'} hover:bg-slate-300 dark:hover:bg-slate-600 hover:shadow-md border-b border-gray-300 dark:border-gray-600`}>
                        <td className="py-2 px-2 text-sm">{row.document_title}</td>
                        <td className="py-2 px-2 text-sm text-center">{row.receivable_number}</td>
                        <td className="py-2 px-2 text-sm text-center">{row.due_date}</td>
                        <td className="py-2 px-2 text-sm text-center text-green-600 dark:text-green-400">{row.amount}</td>
                        <td className="py-2 px-2 text-sm">{row.assignor_name}</td>
                        <td className="py-2 px-2 text-sm">{row.debtor_name}</td>
                        <td className="py-2 px-2 text-sm text-center">
                            <div className="flex justify-center space-x-2">
                                <NewTooltip content="Deletar parcela" alignment="left">
                                    <button
                                        onClick={() => handleDeleteInstallment(row.document_id, row.receivable_number)}
                                        disabled={isDeleting.value}
                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <IoTrash className="w-4 h-4" />
                                    </button>
                                </NewTooltip>
                            </div>
                        </td>
                    </tr>
                ))}
            </ATable>
        );
    };

    const renderDetailView = () => (
        <div className="space-y-4">
            {operation.value.documents.map((document, index) => (
                <div key={index} className="bg-white dark:bg-slate-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-600 p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">Documento {index + 1}</h4>
                        <NewTooltip content="Deletar documento" alignment="left">
                            <button
                                onClick={() => handleDeleteDocument(document.document_id)}
                                disabled={isDeleting.value}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <IoTrash className="w-5 h-5" />
                            </button>
                        </NewTooltip>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-4">
                        <div className="col-span-6">
                            <TextEdit label="ID do Documento:" value={document.document_id} disabled />
                        </div>
                        <div className="col-span-3">
                            <TextEdit label="Título:" value={document.document_title} disabled />
                        </div>
                        <div className="col-span-3">
                            <TextEdit label="CFOP:" value={document.cfop} disabled />
                        </div>
                        <div className="col-span-4">
                            <TextEdit label="Data de Emissão:" value={formatDate(document.issue_date)} disabled />
                        </div>
                        <div className="col-span-4">
                            <TextEdit label="Tamanho do XML:" value={formatBytes(document.xml_size_bytes)} disabled />
                        </div>
                        <div className="col-span-4">
                            <TextEdit label="Recebíveis:" value={operation.value.total_receivables.toString()} disabled />
                        </div>
                    </div>
                    <div className="border-t pt-4 mb-4">
                        <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Dados do Cedente</h4>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-6">
                                <TextEdit label="Nome:" value={document.assignor_name} disabled />
                            </div>
                            <div className="col-span-3">
                                <TextEdit label="CPF/CNPJ:" value={document.assignor_cnpj} disabled />
                            </div>
                        </div>
                    </div>
                    <div className="border-t pt-4 mb-4">
                        <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Dados do Sacado</h4>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-6">
                                <TextEdit label="Nome:" value={document.debtor_name} disabled />
                            </div>
                            <div className="col-span-3">
                                <TextEdit label="CPF/CNPJ:" value={document.debtor_cpf_cnpj} disabled />
                            </div>
                            <div className="col-span-3">
                                <TextEdit label="Telefone:" value={document.address_phone} disabled />
                            </div>
                            <div className="col-span-12">
                                <TextEdit label="Email:" value={document.debtor_email} disabled />
                            </div>
                        </div>
                    </div>

                    <div className="border-t pt-4 mb-4">
                        <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Endereço Sacado</h4>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-6">
                                <TextEdit label="Logradouro:" value={document.address_street} disabled />
                            </div>
                            <div className="col-span-2">
                                <TextEdit label="Número:" value={document.address_number} disabled />
                            </div>
                            <div className="col-span-4">
                                <TextEdit label="Complemento:" value={document.address_complement} disabled />
                            </div>
                            <div className="col-span-4">
                                <TextEdit label="Bairro:" value={document.address_district} disabled />
                            </div>
                            <div className="col-span-4">
                                <TextEdit label="Cidade:" value={document.address_city} disabled />
                            </div>
                            <div className="col-span-2">
                                <TextEdit label="UF:" value={document.address_state} disabled />
                            </div>
                            <div className="col-span-2">
                                <TextEdit label="CEP:" value={document.address_zip_code} disabled />
                            </div>
                        </div>
                    </div>

                    {document.receivables.length > 0 && (
                        <div className="border-t pt-4">
                            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Recebíveis</h4>
                            <div className="space-y-3">
                                {document.receivables.map((receivable, receivableIndex) => (
                                    <div key={receivableIndex} className="bg-gray-50 dark:bg-slate-700 p-3 rounded flex justify-between items-center">
                                        <div className="grid grid-cols-12 gap-4 flex-1">
                                            <div className="col-span-4">
                                                <TextEdit label="Número:" value={receivable.receivable_number} disabled />
                                            </div>
                                            <div className="col-span-4">
                                                <TextEdit label="Vencimento:" value={receivable.due_date.split('T')[0].split('-').reverse().join('/')} disabled />
                                            </div>
                                            <div className="col-span-4">
                                                <TextEdit label="Valor:" value={formatCurrency(receivable.amount)} disabled />
                                            </div>
                                        </div>
                                        <div className="ml-4">
                                            <NewTooltip content="Deletar parcela" alignment="left">
                                                <button
                                                    onClick={() => handleDeleteInstallment(document.document_id, receivable.receivable_number)}
                                                    disabled={isDeleting.value}
                                                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <IoTrash className="w-4 h-4" />
                                                </button>
                                            </NewTooltip>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <PageForm
            pageTitle={operation.value.operation_code}
            apiVersion={apiVersion.value}
            isLoading={isLoading}
            breadcrumb={[
                { label: "Operações", url: "/structuring/operations/0/12" },
                { label: "Detalhes da operação", url: "#" },
            ]}
            controlSlot={
                <div className="flex justify-end">
                    <button
                        className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded"
                        onClick={() => navigate(-1)}
                    >
                        <IoMdArrowRoundBack />
                    </button>

                    <NewTooltip content="Exportar para Excel" alignment="left">
                        <button
                            type="button"
                            className="text-white bg-blue-500 hover:bg-blue-700 font-medium rounded text-sm px-4 py-2 ml-2 flex items-center"
                            onClick={exportToExcelHandler}
                        >
                            <FaFileExcel className="mr-2" />
                            Exportar
                        </button>
                    </NewTooltip>
                </div>
            }
        >
            <div className="container mx-auto p-4">
                <div className="grid grid-cols-12 gap-4 mb-6">
                    <div className="col-span-4">
                        <TextEdit label="Valor Total:" value={formatCurrency(operation.value.total_amount)} disabled />
                    </div>
                    <div className="col-span-4">
                        <TextEdit label="Total de Documentos:" value={operation.value.documents.length.toString()} disabled />
                    </div>
                    <div className="col-span-4">
                        <TextEdit label="Total de Recebíveis:" value={operation.value.total_receivables.toString()} disabled />
                    </div>
                </div>

                {operation.value.notes && (
                    <div className="mb-6">
                        <TextEdit label="Observações:" value={operation.value.notes} disabled />
                    </div>
                )}

                <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <IoDocumentText className="mr-2" />
                            Documentos ({operation.value.documents.length})
                        </h3>

                        <div className="flex space-x-1 bg-gray-100 rounded p-1">
                            <NewTooltip content="Visão em detalhe" alignment="left">
                                <button
                                    type="button"
                                    className={`p-2 rounded ${viewMode.value === 'detail' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                                    onClick={() => viewMode.value = 'detail'}
                                >
                                    <IoGrid className="w-4 h-4" />
                                </button>
                            </NewTooltip>
                            <NewTooltip content="Visão em lista" alignment="left">
                                <button
                                    type="button"
                                    className={`p-2 rounded ${viewMode.value === 'list' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                                    onClick={() => viewMode.value = 'list'}
                                >
                                    <IoList className="w-4 h-4" />
                                </button>
                            </NewTooltip>
                        </div>
                    </div>

                    {viewMode.value === 'detail' ? renderDetailView() : renderTableView()}
                </div>
            </div>

            <Dialog
                id="delete-confirmation"
                show={showDeleteDialog.value}
                onClose={() => showDeleteDialog.value = false}
                header={deleteAction.value === 'document' ? 'Deletar Documento' : 'Deletar Parcela'}
                body={
                    <div className="p-6">
                        <p className="text-gray-700 dark:text-gray-300">
                            {deleteAction.value === 'document'
                                ? 'Tem certeza que deseja deletar este documento? Esta ação não pode ser desfeita. Se for o último documento da operação, a operação inteira será removida.'
                                : 'Tem certeza que deseja deletar esta parcela? Esta ação não pode ser desfeita e as parcelas restantes serão reorganizadas.'
                            }
                        </p>
                    </div>
                }
                footer={
                    <div className="flex space-x-2">
                        <button
                            type="button"
                            onClick={() => showDeleteDialog.value = false}
                            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                            disabled={isDeleting.value}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={confirmDelete}
                            className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
                            disabled={isDeleting.value}
                        >
                            {isDeleting.value ? 'Deletando...' : 'Deletar'}
                        </button>
                    </div>
                }
            />
        </PageForm>
    );
};

export default OperationPageView;
